"use client";

import { useEffect, useMemo, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { getEcho } from "@/lib/echo";
import { logger } from "@/lib/logger";
import { useAuthStore } from "@/modules/auth/stores/auth-store";

import type { WidgetDefinition } from "@/types";

// Coalesce a burst of signals (a single save can fire several) into one refresh.
const DEBOUNCE_MS = 300;

interface DataChangedPayload {
  resources?: string[];
}

interface NotificationPayload {
  type?: string | null;
}

/**
 * Keeps dashboard cards live, per card.
 *
 * Widgets declare which events concern them (`live_on`); this builds the reverse
 * map and, when one fires, invalidates ONLY the affected widgets' queries. No new
 * backend events were needed — both channels already carry these payloads for
 * other features.
 *
 * Each widget owning its own query is what makes this as small as it is. It used
 * to fetch a batch by hand and then patch the result into every batch cache entry
 * that happened to contain one of the refreshed keys, because a widget's data was
 * only ever reachable through a cache entry keyed by the whole tier's key list.
 * Now the key is the widget, so TanStack does the work: invalidate, and any
 * mounted card refetches itself with its own params.
 *
 * Cleanup uses stopListening / stopListeningForNotification rather than
 * `echo.leave()`: both channels are SHARED with the app-wide notification and
 * system-message hooks, and leaving a channel would silently tear down their
 * handlers too.
 */
export function useDashboardRealtime(widgets: WidgetDefinition[]) {
  const qc = useQueryClient();
  const companyUuid = useAuthStore((s) => s.companyAccount?.uuid);
  const userUuid = useAuthStore((s) => s.user?.uuid);

  // resource slug -> widget keys, plus notification subscribers split into
  // "any notification" and per-type.
  const triggers = useMemo(() => {
    const byResource = new Map<string, string[]>();
    const byNotificationType = new Map<string, string[]>();
    const anyNotification: string[] = [];

    for (const widget of widgets) {
      for (const trigger of widget.live_on) {
        if (trigger.kind === "resource" && trigger.resource) {
          push(byResource, trigger.resource, widget.key);
        } else if (trigger.kind === "notification") {
          if (trigger.notification_type) {
            push(byNotificationType, trigger.notification_type, widget.key);
          } else {
            anyNotification.push(widget.key);
          }
        }
      }
    }

    return { byResource, byNotificationType, anyNotification };
  }, [widgets]);

  // The socket handlers read the trigger map through a ref so that changing the
  // widget set doesn't tear down and re-establish the subscriptions.
  //
  // Params are no longer needed here at all: invalidation names the widget, not
  // the values it was last fetched with, so each card refetches with whatever its
  // own controls currently say.
  const triggersRef = useRef(triggers);

  useEffect(() => {
    triggersRef.current = triggers;
  }, [triggers]);

  // Only the channel identity belongs in the subscription's deps; everything
  // else is read via the ref above.
  const hasWidgets = widgets.length > 0;

  useEffect(() => {
    if (!companyUuid || !userUuid || !hasWidgets) return;

    const echo = getEcho();
    if (!echo) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending = new Set<string>();

    const flush = () => {
      const keys = [...pending];
      pending = new Set();

      if (keys.length === 0) return;

      logger.info("[dashboard] live refresh", keys);

      for (const key of keys) {
        // Prefix match, stopping short of the params segment, so a card refreshes
        // whatever range it is currently showing — and any other cached range of
        // the same widget is marked stale rather than left to be restored as
        // yesterday's numbers if the user switches back to it.
        //
        // Not awaited, and nothing to catch: a failed refetch leaves the card
        // showing its last good data, and the next signal recovers it. Surfacing
        // it would be noise.
        void qc.invalidateQueries({
          queryKey: ["dashboard", "widget", companyUuid, key],
        });
      }
    };

    const schedule = (keys: string[]) => {
      if (keys.length === 0) return;
      keys.forEach((key) => pending.add(key));

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        flush();
      }, DEBOUNCE_MS);
    };

    const onDataChanged = (payload: DataChangedPayload) => {
      const resources = payload?.resources ?? [];
      const { byResource } = triggersRef.current;

      if (resources.length === 0) {
        // A broad signal with no hint: refresh every resource-driven widget
        // rather than guess and go stale.
        schedule([...new Set([...byResource.values()].flat())]);
        return;
      }

      schedule([
        ...new Set(
          resources.flatMap((resource) => byResource.get(resource) ?? []),
        ),
      ]);
    };

    const onNotification = (payload: NotificationPayload) => {
      const { byNotificationType, anyNotification } = triggersRef.current;
      const typed = payload?.type
        ? (byNotificationType.get(payload.type) ?? [])
        : [];

      schedule([...new Set([...anyNotification, ...typed])]);
    };

    const companyChannel = echo.private(`company.${companyUuid}`);
    companyChannel.listen(".company.data.changed", onDataChanged);

    const userChannel = echo.private(`company.${companyUuid}.user.${userUuid}`);
    userChannel.notification(onNotification);

    return () => {
      if (timer) clearTimeout(timer);
      companyChannel.stopListening(".company.data.changed", onDataChanged);
      userChannel.stopListeningForNotification(onNotification);
    };
  }, [companyUuid, userUuid, qc, hasWidgets]);
}

function push(map: Map<string, string[]>, key: string, value: string): void {
  const existing = map.get(key);
  if (existing) existing.push(value);
  else map.set(key, [value]);
}
